import { QUEUE_SHELL, QUEUE_SCROLL, QUEUE_EMPTY, QUEUE_GRID_HEAD, QUEUE_TH_TEXT, QUEUE_GRID_ROW_INNER } from './queueTableStyles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { OttoLoading } from '../otto';
import useNegativeMood from '../otto/ottoMood';
import { fmtDate, daysPastDeadline } from '../../lib/dates';

// Cross-brief overdue list (after-the-deadline §4), embedded as a tab on the
// Briefs page. Every live brief past its response deadline with no outcome
// recorded, sorted by days overdue descending. A brief with quotes in is the
// priority case; one that closed with no responses is a different problem and is
// visually distinguished.

export default function BriefsOverdueList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Keep the roamer off this tab while there is anything on it. Every one of
  // his technical gates passes here -- no modal, nothing focused, tab visible --
  // so without this he hops across a list of missed deadlines. An EMPTY overdue
  // list is good news, so the declaration is conditional rather than blanket.
  useNegativeMood(rows.length > 0);

  const load = useCallback(async () => {
    setLoading(true);
    // "Overdue" = deadline's calendar day is before today — the same definition
    // as the Briefs list / Otto bar (deadline < start of today), so the tab count
    // matches Otto's "N overdue" rather than diverging on briefs due earlier today.
    const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
    const { data: briefs } = await supabase
      .from('briefs')
      .select('id, brief_name, fusion_project_name, required_response_time, status, awarded_supplier_id')
      .eq('status', 'sent')
      .is('archived_at', null)
      .lt('required_response_time', startOfToday.toISOString())
      .order('required_response_time', { ascending: true });
    const ids = (briefs || []).map(b => b.id);
    const [respRes, chaseRes] = await Promise.all([
      ids.length ? supabase.from('brief_responses').select('brief_id').eq('is_submitted', true).in('brief_id', ids) : Promise.resolve({ data: [] }),
      ids.length ? supabase.from('brief_update_requests').select('brief_id, supplier_id').in('brief_id', ids) : Promise.resolve({ data: [] }),
    ]);
    const respByBrief = new Map();
    (respRes.data || []).forEach(r => respByBrief.set(r.brief_id, (respByBrief.get(r.brief_id) || 0) + 1));
    const chaseByBrief = new Map();
    (chaseRes.data || []).forEach(r => {
      if (!chaseByBrief.has(r.brief_id)) chaseByBrief.set(r.brief_id, new Set());
      chaseByBrief.get(r.brief_id).add(r.supplier_id);
    });
    setRows((briefs || []).map(b => ({
      ...b,
      responses: respByBrief.get(b.id) || 0,
      chasedBy: chaseByBrief.get(b.id)?.size || 0,
      daysOverdue: daysPastDeadline(b.required_response_time) || 0,
    })));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => rows.slice().sort((a, b) => b.daysOverdue - a.daysOverdue), [rows]);

  if (loading) return <div className="py-20"><OttoLoading size="large" message="Loading overdue briefs" /></div>;

  return (
    <div className="px-8 py-6">
      <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mb-4 max-w-[70ch]">
        Every brief past its response deadline with no award or close recorded. Often the decision's been made and just not entered — a two-second fix. Quotes-in briefs are the priority; ones that closed with no responses are a different problem.
      </p>
      {sorted.length === 0 ? (
        <div className={QUEUE_EMPTY}>
          <p className="font-['Metropolis'] text-base font-semibold text-ink">Nothing overdue</p>
          <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-1">Every sent brief is either within its deadline or already awarded/closed.</p>
        </div>
      ) : (
        <div className={`${QUEUE_SHELL} ${QUEUE_SCROLL}`}>
          <div className={`grid grid-cols-[minmax(220px,1fr)_120px_110px_120px_130px] gap-3 min-w-[760px] ${QUEUE_GRID_HEAD}`}>
            {['Brief', 'Closed', 'Overdue', 'Responses', 'Chased'].map((h) => (
              <div key={h} className={QUEUE_TH_TEXT}>{h}</div>
            ))}
          </div>
          {sorted.map((b, idx) => {
            const noResponses = b.responses === 0;
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => navigate(`/briefs/${b.id}#brief-recipients-section`)}
                className={`w-full text-left grid grid-cols-[minmax(220px,1fr)_120px_110px_120px_130px] gap-3 min-w-[760px] ${QUEUE_GRID_ROW_INNER} ${idx % 2 === 1 ? 'bg-table-zebra' : 'bg-white'} ${noResponses ? 'opacity-70' : ''}`}>
                <div className="min-w-0">
                  <p className="font-['Metropolis'] text-sm text-ink font-semibold truncate">{b.brief_name || 'Brief'}</p>
                  {b.fusion_project_name && <p className="font-['Metropolis'] text-[11px] text-[#51534A] truncate">{b.fusion_project_name}</p>}
                </div>
                <div className="font-['Metropolis'] text-sm text-ink self-center">{fmtDate(b.required_response_time)}</div>
                <div className="self-center">
                  <span className={`font-['Metropolis'] text-[11px] font-semibold rounded-md px-2 py-0.5 border ${b.daysOverdue >= 4 ? 'text-[#E0290A] bg-[#E0290A]/10 border-[#E0290A]/40' : 'text-[#7A4E10] bg-[#E89A2E]/15 border-[#E89A2E]/40'}`}>
                    {b.daysOverdue} day{b.daysOverdue === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="self-center">
                  {noResponses ? (
                    <span className="font-['Metropolis'] text-[11px] font-semibold text-[#51534A] bg-[#51534A]/10 border border-[#DADCCD] rounded-md px-2 py-0.5">No responses</span>
                  ) : (
                    <span className="font-['Metropolis'] text-sm text-fusion-forest font-semibold">{b.responses} quote{b.responses === 1 ? '' : 's'}</span>
                  )}
                </div>
                <div className="self-center">
                  {b.chasedBy > 0
                    ? <span className="font-['Metropolis'] text-[11px] font-semibold text-[#7A4E10] bg-[#E89A2E]/15 border border-[#E89A2E]/40 rounded-md px-2 py-0.5">{b.chasedBy} asked</span>
                    : <span className="font-['Metropolis'] text-sm text-[#51534A]">—</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
